import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Calculator, Settings, FileText, ChevronRight, ArrowRight } from 'lucide-react';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

const DimensioneringHubPage = () => {
  const { language } = useLanguage();

  const tools = [
    {
      id: 'ackumulatortank',
      title: 'Dimensionera Ackumulatortank',
      description: 'Beräkna rätt tankvolym baserat på systemeffekt, temperaturdifferens och bufferttid. Få produktrekommendation direkt.',
      icon: Calculator,
      href: '/dimensionering/ackumulatortank',
      status: 'active',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      id: 'expansion',
      title: 'Expansions- och Tryckhållningssystem',
      description: 'Dimensionera slutna expansionskärl enligt SS-EN 12828: nominell volym, förtryck och sluttryck – med expansionstabell och räkneexempel.',
      icon: Settings,
      href: '/dimensionering/expansionskarl',
      status: 'active',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'specialtank',
      title: 'Designa Specialtank',
      description: 'Skräddarsy din ackumulatortank med valfria anslutningar, volym och material. Skicka förfrågan direkt till oss.',
      icon: FileText,
      href: '/dimensionering/specialtank',
      status: 'active',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-24">
      <SEO 
        title="Dimensioneringsverktyg | Flowingo AB"
        description="Intelligenta dimensioneringsverktyg för VVS-system. Beräkna ackumulatortankar, expansionssystem och designa specialtankar."
        language={language}
        schema={[
          generateBreadcrumbSchema([
            { name: 'Hem', url: 'https://flowingo.se' },
            { name: 'Dimensionering', url: 'https://flowingo.se/dimensionering' },
          ]),
        ]}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-8">
          <Link to="/" className="hover:text-stone-900">Hem</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-stone-900">Dimensionering</span>
        </nav>

        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-stone-100 px-4 py-2 rounded-full mb-6">
            <Calculator className="w-4 h-4 text-[#321a27]" />
            <span className="text-sm font-medium text-stone-700">Professionella verktyg</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 mb-6">
            Dimensioneringsverktyg
          </h1>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto">
            Använd våra intelligenta verktyg för att dimensionera VVS-system korrekt. 
            Få produktrekommendationer baserade på tekniska beräkningar och branschstandarder.
          </p>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {tools.map((tool) => (
            <div
              key={tool.id}
              className="group relative bg-white border-2 border-stone-200 hover:border-[#321a27] transition-all duration-300 overflow-hidden"
            >
              {/* Gradient Header */}
              <div className={`bg-gradient-to-br ${tool.color} p-8 text-white relative`}>
                <tool.icon className="w-16 h-16 mb-4 opacity-90" />
                <h2 className="text-2xl font-bold mb-2">{tool.title}</h2>
                
                {tool.status === 'coming' && (
                  <span className="absolute top-4 right-4 bg-white/20 backdrop-blur text-white text-xs px-3 py-1 rounded-full">
                    Kommer snart
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="p-8">
                <p className="text-stone-600 mb-6 min-h-[80px]">
                  {tool.description}
                </p>

                {tool.status === 'active' ? (
                  <Link to={tool.href}>
                    <button className="w-full bg-[#321a27] hover:bg-[#4a2d3d] text-white py-3 px-6 flex items-center justify-center gap-2 transition-colors">
                      Starta verktyg
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </Link>
                ) : (
                  <button 
                    disabled 
                    className="w-full bg-stone-200 text-stone-500 py-3 px-6 cursor-not-allowed"
                  >
                    Kommer snart
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div className="bg-stone-50 border border-stone-200 p-8">
          <h3 className="text-2xl font-semibold text-stone-900 mb-4">
            Tekniskt korrekta beräkningar
          </h3>
          <p className="text-stone-600 mb-6">
            Våra dimensioneringsverktyg baseras på branschstandarder, produktblad och tekniska specifikationer. 
            Alla beräkningar följer gällande normer och rekommendationer för VVS-installationer.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-[#321a27] rounded-full mt-2" />
              <div>
                <p className="font-semibold text-stone-900">SS-EN standarder</p>
                <p className="text-sm text-stone-600">Enligt gällande europeiska standarder</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-[#321a27] rounded-full mt-2" />
              <div>
                <p className="font-semibold text-stone-900">Produktbaserat</p>
                <p className="text-sm text-stone-600">Matchas mot vårt sortiment</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-[#321a27] rounded-full mt-2" />
              <div>
                <p className="font-semibold text-stone-900">Installatörsanpassat</p>
                <p className="text-sm text-stone-600">För professionell användning</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DimensioneringHubPage;
